import React, { useState, useEffect } from 'react';
import { User, Phone, Mail, Search, Eye, MapPin, Users, Copy, FileText } from 'lucide-react';
import { apiService, ServiceRequest, ServiceRequestStatus } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import Modal from '../components/Modal';

const ServiceRequests: React.FC = () => {
  const { showToast } = useToast();
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal State
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await apiService.getServiceRequests();
      setRequests(data || []);
    } catch (error) {
      console.error('Failed to fetch service requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: ServiceRequestStatus) => {
    try {
      await apiService.updateServiceRequest(id, { status });
      setRequests(requests.map(req =>
        req.id === id ? { ...req, status } : req
      ));
      showToast('Status updated successfully!', 'success');

      if (selectedRequest && selectedRequest.id === id) {
        setSelectedRequest({ ...selectedRequest, status });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      showToast('Failed to update status.', 'error');
    }
  };

  const handleViewDetails = (request: ServiceRequest) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-[10px] font-bold uppercase">Pending</span>;
      case 'confirmed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-[10px] font-bold uppercase">Confirmed</span>;
      case 'in_progress':
        return <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-[10px] font-bold uppercase">In-Site</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-[10px] font-bold uppercase">Completed</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-[10px] font-bold uppercase">Cancelled</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-[10px] font-bold uppercase">{status}</span>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return <span className="px-2 py-0.5 bg-red-600 text-white rounded text-[9px] font-bold uppercase">Urgent</span>;
      case 'high':
        return <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-[9px] font-bold uppercase">High</span>;
      case 'medium':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[9px] font-bold uppercase">Medium</span>;
      case 'low':
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-400 rounded text-[9px] font-bold uppercase">Low</span>;
      default: return null;
    }
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = (req.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (req.service_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Service Requests</h1>
          <p className="text-gray-600">Manage bookings and work orders for electrical services</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500/20 shadow-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500/20"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-50">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Customer</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Service</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Priority</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Date</th>
              <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Update</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredRequests.map((request) => (
              <tr key={request.id} className="hover:bg-gray-50/50 transition">
                <td className="px-6 py-4">
                  <div className="text-sm font-bold text-gray-900 leading-none mb-1">{request.customer_name}</div>
                  <div className="text-[10px] text-gray-400 flex items-center">
                    <Mail className="h-2.5 w-2.5 mr-1" /> {request.customer_email}
                  </div>
                  <div className="text-[10px] text-orange-600 flex items-center mt-0.5">
                    <Phone className="h-2.5 w-2.5 mr-1" /> {request.customer_phone}
                  </div>
                </td>
                <td className="px-6 py-4 font-medium max-w-sm">
                  <div className="text-sm font-medium text-gray-800">{request.service_name}</div>
                  <div className="text-[10px] text-gray-400 italic mt-0.5 line-clamp-2">
                    {request.problem_description}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {getPriorityBadge(request.priority)}
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(request.status)}
                </td>
                <td className="px-6 py-4 text-xs text-gray-500 font-medium">
                  {new Date(request.preferred_date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleViewDetails(request)}
                      className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <select
                      value={request.status}
                      onChange={(e) => updateStatus(request.id, e.target.value as ServiceRequestStatus)}
                      className="border border-gray-100 rounded px-2 py-1 text-[10px] font-bold uppercase outline-none bg-white focus:border-orange-200"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirm</option>
                      <option value="in_progress">Dispatch</option>
                      <option value="completed">Complete</option>
                      <option value="cancelled">Cancel</option>
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredRequests.length === 0 && (
          <div className="p-12 text-center text-gray-400 text-sm italic">Pipeline is empty based on filters.</div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Service Request Details"
        maxWidth="max-w-4xl"
      >
        {selectedRequest && (
          <div className="space-y-8">
            {/* Header / Status Section */}
            <div className="flex flex-col sm:flex-row items-start justify-between gap-6">
              <div className="space-y-3 flex-1">
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-orange-600 uppercase tracking-[0.2em] leading-none mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                    Requested Service
                  </p>
                  <h3 className="text-4xl font-black text-gray-900 leading-tight tracking-tight">{selectedRequest.service_name || 'General Electrical Service'}</h3>
                </div>

                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedRequest.status)}
                  {getPriorityBadge(selectedRequest.priority)}
                </div>
              </div>

              <div className="text-left sm:text-right bg-blue-50/80 backdrop-blur-sm p-5 rounded-2xl border border-blue-100/50 shadow-sm min-w-[200px] flex flex-col justify-center">
                <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-2 font-black shadow-sm">Preferred Date</p>
                <p className="text-2xl font-black text-gray-900 leading-none">{new Date(selectedRequest.preferred_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                <p className="text-xs text-blue-600 font-bold mt-1.5">{new Date(selectedRequest.preferred_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            </div>

            {/* Customer & Location Section */}
            <div className="p-8 bg-gray-50/50 rounded-[2.5rem] border border-gray-100 space-y-6 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-10 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity pointer-events-none">
                <Users className="w-32 h-32 text-gray-900" />
              </div>

              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                <User className="w-3 h-3" />
                Customer & Location
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12 relative z-10">
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Client Name</p>
                  <p className="text-xl font-black text-gray-900">{selectedRequest.customer_name}</p>
                </div>

                <div className="space-y-1.5 group/item">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Phone Terminal</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(selectedRequest.customer_phone);
                        showToast('Phone number copied!', 'success');
                      }}
                      className="opacity-0 group-hover/item:opacity-100 p-1 text-gray-400 hover:text-orange-600 transition-all rounded"
                      title="Copy Phone"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <a href={`tel:${selectedRequest.customer_phone}`} className="text-2xl font-black text-gray-900 hover:text-orange-600 transition-colors flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center">
                      <Phone className="w-4 h-4 text-orange-500" />
                    </div>
                    {selectedRequest.customer_phone}
                  </a>
                </div>

                <div className="space-y-1.5 group/item">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Email Address</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(selectedRequest.customer_email);
                        showToast('Email copied!', 'success');
                      }}
                      className="opacity-0 group-hover/item:opacity-100 p-1 text-gray-400 hover:text-orange-600 transition-all rounded"
                      title="Copy Email"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <a href={`mailto:${selectedRequest.customer_email}`} className="text-lg font-bold text-orange-600 hover:text-orange-700 transition-colors flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-orange-500" />
                    </div>
                    {selectedRequest.customer_email}
                  </a>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Primary Location</p>
                  <div className="text-lg font-bold text-gray-900 flex items-start gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-blue-500" />
                    </div>
                    <span className="break-words leading-tight pt-1">{selectedRequest.address || 'Address not provided'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="space-y-4">
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2 px-2">
                <FileText className="w-3 h-3" />
                Issue Description
              </h4>
              <div className="p-10 bg-white border border-gray-100 rounded-[2.5rem] text-gray-700 leading-relaxed shadow-[0_20px_50px_rgba(0,0,0,0.04)] relative">
                <div className="absolute top-6 left-6 text-gray-100 opacity-50">
                  <svg className="w-12 h-12 fill-current" viewBox="0 0 24 24">
                    <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H16.017C14.9124 8 14.017 7.10457 14.017 6V5C14.017 3.89543 14.9124 3 16.017 3H19.017C21.2261 3 23.017 4.79086 23.017 7V15C23.017 18.3137 20.3307 21 17.017 21H14.017ZM1.017 21L1.017 18C1.017 16.8954 1.91243 16 3.017 16H6.017C6.56928 16 7.017 15.5523 7.017 15V9C7.017 8.44772 6.56928 8 6.017 8H3.017C1.91243 8 1.017 7.10457 1.017 6V5C1.017 3.89543 1.91243 3 3.017 3H6.017C8.22614 3 10.017 4.79086 10.017 7V15C10.017 18.3137 7.33071 21 4.017 21H1.017Z" />
                  </svg>
                </div>
                <div className="absolute bottom-6 right-6 text-gray-100 opacity-50 rotate-180">
                  <svg className="w-12 h-12 fill-current" viewBox="0 0 24 24">
                    <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H16.017C14.9124 8 14.017 7.10457 14.017 6V5C14.017 3.89543 14.9124 3 16.017 3H19.017C21.2261 3 23.017 4.79086 23.017 7V15C23.017 18.3137 20.3307 21 17.017 21H14.017ZM1.017 21L1.017 18C1.017 16.8954 1.91243 16 3.017 16H6.017C6.56928 16 7.017 15.5523 7.017 15V9C7.017 8.44772 6.56928 8 6.017 8H3.017C1.91243 8 1.017 7.10457 1.017 6V5C1.017 3.89543 1.91243 3 3.017 3H6.017C8.22614 3 10.017 4.79086 10.017 7V15C10.017 18.3137 7.33071 21 4.017 21H1.017Z" />
                  </svg>
                </div>
                <div className="relative z-10 font-medium italic text-gray-600 text-center text-xl px-4 whitespace-pre-wrap break-all md:break-words">
                  {selectedRequest.problem_description}
                </div>
              </div>
            </div>

            {/* Status Actions */}
            <div className="pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <span className="text-sm text-gray-500 font-bold uppercase tracking-wider">Update Status:</span>
              <div className="flex gap-2 flex-wrap justify-center">
                {(['pending', 'confirmed', 'in_progress', 'completed', 'cancelled'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => updateStatus(selectedRequest.id, status)}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${selectedRequest.status === status
                      ? 'bg-gray-900 text-white shadow-lg shadow-gray-200 -translate-y-0.5'
                      : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300'
                      }`}
                  >
                    {status.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ServiceRequests;